import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, Loader2, ArrowRight, AlertTriangle, Wrench } from "lucide-react";
import { analyzeWebsite, countryOpportunities } from "@/lib/api";
import { AuditReportEmail } from "@/components/AuditReportEmail";
import LeadGenReadiness from "@/components/LeadGenReadiness";

const CATS = [
  ["seo", "SEO"], ["performance", "Performance"], ["mobile", "Mobile"], ["accessibility", "Accessibility"],
  ["content", "Content"], ["conversion", "Conversion"], ["security", "Security"], ["ai_readiness", "AI Readiness"],
];

const band = (s) => (s >= 85 ? ["Excellent", "text-emerald-600"] : s >= 70 ? ["Good", "text-blue-600"] : s >= 50 ? ["Needs Improvement", "text-amber-600"] : s >= 30 ? ["Poor", "text-orange-600"] : ["Critical", "text-rose-600"]);

export default function IndiaAudit() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [opps, setOpps] = useState([]);
  const [err, setErr] = useState("");

  const run = async (e) => {
    e?.preventDefault();
    if (!url.trim()) return;
    setLoading(true); setErr(""); setRes(null); setOpps([]);
    try {
      const r = await analyzeWebsite(url.trim());
      setRes(r);
      try {
        const o = await countryOpportunities("in", { categories: r.categories, content_readable: r.content_readable, domain: r.domain });
        setOpps(o.opportunities || []);
      } catch { /* opportunities are best-effort */ }
    }
    catch { setErr("Couldn't analyze that URL. Please check it and try again."); }
    setLoading(false);
  };

  const [label, color] = res ? band(res.overall_score) : ["", ""];

  return (
    <div data-testid="india-audit">
      <section className="bg-gradient-to-br from-[#0B203E] to-[#071428] text-white py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold">Free Website Health Check</h1>
          <p className="text-blue-100/80 mt-4 md:text-lg">Enter your website and get your WebsiteTech Score across SEO, performance, mobile, security & AI-readiness — with actionable recommendations.</p>
          <form onSubmit={run} className="mt-8 flex flex-col sm:flex-row gap-3">
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="yourbusiness.in" data-testid="india-audit-input"
              className="flex-1 rounded-xl px-4 py-3.5 text-slate-900 outline-none" />
            <button type="submit" disabled={loading} data-testid="india-audit-submit"
              className="bg-white text-[#0B203E] font-semibold rounded-xl px-6 py-3.5 inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <><Search size={17} /> Check now</>}
            </button>
          </form>
          {err && <p className="text-rose-300 text-sm mt-3" data-testid="india-audit-error">{err}</p>}
        </div>
      </section>

      {res && (
        <section className="py-14 bg-white" data-testid="india-audit-result">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {res.content_readable === false && (
              <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3.5 mb-8">
                <AlertTriangle size={17} className="text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-800">We couldn't fully read <span className="font-semibold">{res.domain}</span>{res.block_reason === "thin-or-js-rendered" ? " (it's mostly JavaScript-rendered)" : ""}. Scores are conservative estimates.</p>
              </div>
            )}
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="text-center shrink-0">
                <div className="font-heading text-6xl font-extrabold text-slate-900">{res.overall_score}<span className="text-2xl text-slate-400">/100</span></div>
                <p className={`font-bold mt-1 ${color}`}>{label}</p>
                <p className="text-xs text-slate-400 mt-0.5">{res.domain}</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-1 w-full">
                {CATS.map(([k, lbl]) => (
                  <div key={k} className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-center">
                    <p className="font-heading text-xl font-bold text-slate-900">{res.categories?.[k] ?? "—"}</p>
                    <p className="text-[11px] uppercase tracking-wide text-slate-400">{lbl}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-10">
              <LeadGenReadiness url={res.final_url || res.url || url.trim()} source="in-audit" />
            </div>
            {(res.recommendations || []).length > 0 && (
              <div className="mt-10">
                <h3 className="font-heading text-lg font-bold text-slate-900 mb-3">Top recommendations</h3>
                <ul className="space-y-2">
                  {res.recommendations.slice(0, 5).map((r, i) => (
                    <li key={i} className="bg-blue-50/60 border border-blue-100 rounded-lg px-4 py-3 text-sm text-slate-700">
                      <span className="font-semibold">{r.title || r.category}:</span> {r.detail || r.recommendation || r.description}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {opps.length > 0 && (
              <div className="mt-10" data-testid="india-opportunities">
                <h3 className="font-heading text-lg font-bold text-slate-900 mb-1 flex items-center gap-2"><Wrench size={17} className="text-brand" /> Your growth opportunities</h3>
                <p className="text-sm text-slate-500 mb-4">Based on your score, here's what we'd fix — and the WebsiteTech India service that does it.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {opps.map((o, i) => (
                    <div key={i} className="bg-white border border-slate-200 rounded-xl p-4 card-shadow flex flex-col" data-testid="india-opportunity">
                      <p className="text-sm text-slate-500">You're missing</p>
                      <p className="font-semibold text-slate-900">{o.problem}</p>
                      <p className="text-sm text-brand font-semibold mt-2">→ {o.service}</p>
                      <Link to={`/in/contact?service=${encodeURIComponent(o.service)}${o.package ? `&package=${encodeURIComponent(o.package)}` : ""}`}
                        data-testid="opportunity-cta"
                        className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-brand rounded-lg px-3.5 py-2 self-start hover:-translate-y-0.5 transition-transform">
                        {o.cta} <ArrowRight size={14} />
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-10 max-w-2xl">
              <AuditReportEmail auditId={res.id} brand="in" />
            </div>
            <div className="mt-10 text-center bg-slate-50 rounded-2xl p-8">
              <h3 className="font-heading text-xl font-bold text-slate-900">Want us to fix these for you?</h3>
              <p className="text-slate-500 mt-2">Get a tailored ₹ proposal from WebsiteTech India.</p>
              <Link to="/in/contact?service=Website%20Health%20Check" className="mt-5 inline-flex items-center gap-2 bg-brand text-white font-semibold rounded-xl px-6 py-3.5 hover:-translate-y-0.5 transition-transform">Get my proposal <ArrowRight size={17} /></Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
